import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Combobox } from '@/components/ui/Combobox';
import { toast } from '@/components/ui/Toast';
import { routesApi, shopsApi, type ShopInput } from '@/services/endpoints/masterData';
import { queryKeys } from '@/services/queryKeys';
import { ApiError } from '@/services/apiClient';
import type { Shop } from '@/domain/types';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** Absent when creating. */
  shop?: Shop | null;
}

const EMPTY: ShopInput = {
  name: '',
  contactPerson: '',
  contactNumber: '',
  addressLine1: '',
  addressLine2: '',
  addressLine3: '',
  routeId: null,
};

export function ShopFormModal({ isOpen, onClose, shop }: Props): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [form, setForm] = useState<ShopInput>(EMPTY);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!isOpen) return;

    setErrors({});
    setForm(
      shop
        ? {
            name: shop.name,
            contactPerson: shop.contactPerson ?? '',
            contactNumber: shop.contactNumber ?? '',
            addressLine1: shop.addressLine1 ?? '',
            addressLine2: shop.addressLine2 ?? '',
            addressLine3: shop.addressLine3 ?? '',
            routeId: shop.routeId,
          }
        : EMPTY,
    );
  }, [isOpen, shop]);

  const mutation = useMutation({
    mutationFn: (input: ShopInput) => (shop ? shopsApi.update(shop.id, input) : shopsApi.create(input)),
    onSuccess: () => {
      // Invalidate the whole shops tree: the list, any detail, and the
      // typeahead cache all reflect this change.
      void queryClient.invalidateQueries({ queryKey: queryKeys.shops.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.routes.all });
      toast.success(shop ? t('shop.updated') : t('shop.created'));
      onClose();
    },
    onError: (error) => {
      if (error instanceof ApiError && error.fieldErrors.length > 0) {
        setErrors(
          Object.fromEntries(error.fieldErrors.map((issue) => [issue.field, issue.message])),
        );
      } else {
        toast.error(error instanceof ApiError ? error.message : t('errors.unknown'));
      }
    },
  });

  const update = <K extends keyof ShopInput>(key: K, value: ShopInput[K]) =>
    setForm((previous) => ({ ...previous, [key]: value }));

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={shop ? t('shop.edit') : t('shop.new')}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={() => mutation.mutate(form)}
            isLoading={mutation.isPending}
            disabled={!form.name.trim()}
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {/* Only the client name is required, per the requirements. */}
        <Input
          label={t('shop.clientName')}
          value={form.name}
          onChange={(event) => update('name', event.target.value)}
          error={errors.name}
          required
          autoFocus
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label={t('shop.contactPerson')}
            value={form.contactPerson ?? ''}
            onChange={(event) => update('contactPerson', event.target.value)}
          />
          <Input
            label={t('shop.contactNumber')}
            value={form.contactNumber ?? ''}
            onChange={(event) => update('contactNumber', event.target.value)}
            inputMode="tel"
          />
        </div>

        <div className="space-y-2">
          <p className="text-sm font-medium text-slate-700">{t('shop.address')}</p>
          {([1, 2, 3] as const).map((line) => (
            <Input
              key={line}
              aria-label={t('shop.addressLine', { n: line })}
              placeholder={t('shop.addressLine', { n: line })}
              value={form[`addressLine${line}` as keyof ShopInput] as string}
              onChange={(event) => update(`addressLine${line}` as keyof ShopInput, event.target.value)}
            />
          ))}
        </div>

        <Combobox
          label={t('shop.route')}
          value={form.routeId ?? null}
          initialLabel={shop?.routeName ?? ''}
          onChange={(id) => update('routeId', id)}
          onSearch={async (term) => {
            const results = await routesApi.suggest(term);
            return results.map((route) => ({
              id: route.id,
              label: route.name,
              sublabel: route.agentName ?? t('route.noAgent'),
            }));
          }}
          hint={t('shop.noRouteWarning')}
        />
      </div>
    </Modal>
  );
}
